import { useEffect, useState } from "react";
import { Reveal, Eyebrow, Check } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";
import { openEnroll } from "@/lib/checkout";

const COHORT_START = new Date("2026-11-03T23:59:59");

const useCountdown = () => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, COHORT_START.getTime() - now);
  return {
    d: Math.floor(diff / 86400000),
    h: Math.floor((diff % 86400000) / 3600000),
    m: Math.floor((diff % 3600000) / 60000),
    s: Math.floor((diff % 60000) / 1000),
    done: diff === 0,
  };
};

const Countdown = () => {
  const { d, h, m, s, done } = useCountdown();
  if (done) {
    return (
      <div
        className="mx-auto mt-6 border border-ink/10 px-5 py-3"
        data-testid="enrollment-countdown"
      >
        <p className="text-[11px] uppercase tracking-[0.18em] text-stone">
          The first cohort is live — applications open for the next
        </p>
      </div>
    );
  }
  return (
    <div
      className="mx-auto mt-6 inline-flex items-center gap-4 border border-ink/10 px-5 py-3"
      data-testid="enrollment-countdown"
    >
      <p className="text-[11px] uppercase tracking-[0.18em] text-stone">
        Enrolling until Nov 3
      </p>
      <span className="hidden h-3 w-px bg-ink/20 sm:block" />
      <p
        className="font-mont text-sm font-bold tabular-nums text-ink"
        data-testid="countdown-timer"
      >
        {d}d {String(h).padStart(2, "0")}h {String(m).padStart(2, "0")}m{" "}
        {String(s).padStart(2, "0")}s
      </p>
    </div>
  );
};

const onEnroll = () => {
  openEnroll();
};

const PERKS = [
  "All 8 weeks, live",
  "Weekly group calls & hot seats",
  "Private community",
  "Direct feedback on your brand",
];

export const Pricing = () => (
  <section
    id="pricing"
    className="bg-white py-24 lg:py-32"
    data-testid="pricing-section"
  >
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <div className="text-center">
          <Eyebrow light>Enrollment</Eyebrow>
          <h2 className="mx-auto mt-6 max-w-2xl font-mont text-3xl font-bold leading-[1.15] tracking-tight text-ink sm:text-4xl lg:text-5xl">
            One program.{" "}
            <em className="italic text-forest">Two</em> ways in.
          </h2>
          <div
            className="mx-auto mt-8 inline-flex items-center gap-3 border border-forest/30 px-5 py-3"
            data-testid="founding-rate-banner"
          >
            <span className="inline-block h-1.5 w-1.5 bg-forest" />
            <p className="text-xs uppercase tracking-[0.18em] text-ink sm:text-sm">
              The first 6 seats are $1,500 — after that it&rsquo;s $1,750
            </p>
          </div>
          <Countdown />
        </div>
      </Reveal>

      <div className="mx-auto mt-14 grid max-w-4xl grid-cols-1 gap-8 md:grid-cols-2">
        <Reveal delay={0.05} className="h-full">
          <div className="flex h-full flex-col bg-ink p-8 text-bone lg:p-10" data-testid="pricing-card-full">
            <p className="text-[11px] uppercase tracking-[0.28em] text-white/50">
              Founding rate — first 6 seats
            </p>
            <div className="mt-6 flex items-end gap-3">
              <p className="font-serif text-6xl font-light leading-none lg:text-7xl">
                $1,500
              </p>
              <p className="pb-1 font-serif text-xl italic text-stone line-through">
                $1,750
              </p>
            </div>
            <p className="mt-2 text-sm text-white/55">Pay in full, USD</p>
            <ul className="mt-8 flex-1 space-y-3.5 border-t border-white/10 pt-8">
              {PERKS.map((p) => (
                <li key={p} className="flex items-center gap-3 font-mont text-sm font-bold text-white/70">
                  <Check />
                  {p}
                </li>
              ))}
            </ul>
            <button
              data-testid="pricing-enroll-full-button"
              onClick={() => onEnroll("full")}
              className="mt-10 bg-forest px-8 py-4 font-mont text-[12px] font-bold uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-[#2a6b50]"
            >
              Enroll now — pay in full
            </button>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="h-full">
          <div className="flex h-full flex-col border border-ink/15 bg-white p-8 lg:p-10" data-testid="pricing-card-plan">
            <p className="text-[11px] uppercase tracking-[0.28em] text-stone">
              Payment plan
            </p>
            <div className="mt-6 flex items-end gap-2">
              <p className="font-serif text-6xl font-light leading-none text-ink lg:text-7xl">
                2&times;$800
              </p>
            </div>
            <p className="mt-2 text-sm text-stone">
              Two monthly payments — $1,600 total
            </p>
            <ul className="mt-8 flex-1 space-y-3.5 border-t border-ink/10 pt-8">
              {PERKS.map((p) => (
                <li key={p} className="flex items-center gap-3 font-mont text-sm font-bold text-[#5c5c5c]">
                  <Check />
                  {p}
                </li>
              ))}
            </ul>
            <button
              data-testid="pricing-enroll-plan-button"
              onClick={() => onEnroll("plan")}
              className="mt-10 border border-ink px-8 py-4 font-mont text-[12px] font-bold uppercase tracking-[0.22em] text-ink transition-colors duration-300 hover:bg-ink hover:text-bone"
            >
              Enroll with payment plan
            </button>
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.2}>
        <p className="mt-10 text-center text-sm text-stone">
          Not sure yet?{" "}
          <button
            data-testid="pricing-apply-link"
            onClick={() => scrollToId("apply")}
            className="underline underline-offset-4 transition-colors hover:text-forest"
          >
            Apply for a spot
          </button>{" "}
          instead — applications are read personally.
        </p>
      </Reveal>
    </div>
  </section>
);

export default Pricing;
